import { Navigate, Route, Routes } from "react-router";

import { AdminRoute, ProtectedRoute } from "@/components/protected-route";
import { AdminActivityPage } from "@/features/admin/activity";
import { AdminUsersPage } from "@/features/admin/users";
import { ChatPage } from "@/features/chat";
import { DashboardPage } from "@/features/dashboard";
import { GuideAdminPage } from "@/features/guide/admin";
import { GuideShell } from "@/features/guide/guide-shell";
import { GuideHowItWorksPage } from "@/features/guide/how-it-works";
import { GuideLocationPage } from "@/features/guide/location";
import { GuideNonMoversPage } from "@/features/guide/nonmovers";
import { GuideOverviewPage } from "@/features/guide/overview";
import { GuideScenariosPage } from "@/features/guide/scenarios";
import { LoginPage } from "@/features/login";
import { NotFoundPage } from "@/features/not-found";
import { ProfilePage } from "@/features/profile";
import { ReportScreen } from "@/features/sku-velocity/reports";
import { ScenariosScreen } from "@/features/sku-velocity/scenarios";
import { SkuVelocityShell } from "@/features/sku-velocity/shell";
import { VerifyEmailPage } from "@/features/verify-email";
import { VotePage } from "@/features/voting/vote-page";

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/scenarios" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Public, unauthenticated demo-voting page — attendees land here from a QR code, never
          through the login gate. */}
      <Route path="/vote" element={<VotePage />} />
      <Route element={<ProtectedRoute />}>
        {/* SKU Velocity sits behind the login — ProtectedRoute bounces
            unauthenticated visitors (including "/") to /login. Every screen renders inside the
            same shell so the nav rail (and its collapse state) stays put. The rail carries the
            agent-first surfaces (Dashboard, Chat, Guide) alongside the unified /scenarios screen
            (merges the former SKU velocity field + Capacity scenarios) and the two review reports
            /reports/storage (Location review) and /reports/nonmover (Non-movers). */}
        <Route element={<SkuVelocityShell />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/scenarios" element={<ScenariosScreen />} />
          <Route path="/reports/:reportId" element={<ReportScreen />} />
          {/* Admin lives inside the shell so the rail + top bar persist; the nested
              AdminRoute still gates it to admins (redirects everyone else). */}
          <Route element={<AdminRoute />}>
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/activity" element={<AdminActivityPage />} />
          </Route>
          {/* The in-app guide — a topic rail beside the matched child page. Its own Admin
              topic route-guards itself; GuideShell just hides the nav entry for non-admins.
              The Surfaces topics mirror the rail's screens; the Ask-the-agent
              and Dashboard topics were retired with those surfaces (see overview.tsx). */}
          <Route path="/guide" element={<GuideShell />}>
            <Route index element={<GuideOverviewPage />} />
            <Route path="how-it-works" element={<GuideHowItWorksPage />} />
            <Route path="scenarios" element={<GuideScenariosPage />} />
            {/* Back-compat for anything that still links/bookmarks the old topic paths. */}
            <Route path="velocity" element={<Navigate to="/guide/scenarios" replace />} />
            <Route path="capacity" element={<Navigate to="/guide/scenarios" replace />} />
            <Route path="location" element={<GuideLocationPage />} />
            <Route path="nonmovers" element={<GuideNonMoversPage />} />
            <Route path="admin" element={<GuideAdminPage />} />
          </Route>
        </Route>
        {/* Back-compat for anything that still links/bookmarks the old paths. */}
        <Route path="/home" element={<Navigate to="/scenarios" replace />} />
        <Route path="/velocity" element={<Navigate to="/scenarios" replace />} />
        <Route path="/capacity" element={<Navigate to="/scenarios" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
